import React, { useState } from 'react';
import { FocusPicker } from '../CustomFocus';
import { focusTemplatesForAltitude } from '../../utils/focusTemplates';
import './GuidanceModeSelector.css';

export default function GuidanceModeSelector({
  mode,
  onModeChange,
  template,
  onTemplateChange,
  focusTargetName,
  onFocusTargetNameChange,
  existingLoglineSession, // If true, disable logline option
  hideModePicker = false,  // If true, only show focus options
  storyId,  // For custom focus support
  projectId,  // For custom focus support
  customFocus,  // Currently selected custom focus
  onCustomFocusChange,  // Callback when custom focus changes
  useNewFocusPicker = false,  // Feature flag for new picker
  seriesAltitude = false,
}) {
  const templates = focusTemplatesForAltitude(seriesAltitude);
  const showPicker = useNewFocusPicker && (storyId || (seriesAltitude && projectId));
  const [showFocusOptions, setShowFocusOptions] = useState(mode === 'focus' || hideModePicker);

  const handleModeSelect = (newMode) => {
    onModeChange(newMode);
    setShowFocusOptions(newMode === 'focus');
    if (newMode === 'explore') {
      onTemplateChange('open');
      onFocusTargetNameChange('');
      if (onCustomFocusChange) onCustomFocusChange(null);
    } else {
      // Focus mode: reset template so user must choose
      onTemplateChange(null);
      onFocusTargetNameChange('');
      if (onCustomFocusChange) onCustomFocusChange(null);
    }
  };

  const handleTemplateSelect = (templateKey) => {
    onTemplateChange(templateKey);
    if (onCustomFocusChange) onCustomFocusChange(null);
  };

  const handleCustomFocusSelect = (focus) => {
    if (onCustomFocusChange) {
      onCustomFocusChange(focus);
      // When selecting custom focus, set template to 'custom'
      if (focus) {
        onTemplateChange('custom');
      }
    }
  };

  return (
    <div className="guidance-mode-selector">
      {!hideModePicker && (
        <div className="mode-buttons">
          <button
            className={`mode-btn ${mode === 'explore' ? 'active' : ''}`}
            onClick={() => handleModeSelect('explore')}
          >
            <span className="mode-name">Explore</span>
            <span className="mode-desc">Follow your curiosity</span>
          </button>
          <button
            className={`mode-btn ${mode === 'focus' ? 'active' : ''}`}
            onClick={() => handleModeSelect('focus')}
          >
            <span className="mode-name">Focus</span>
            <span className="mode-desc">Build depth in an element</span>
          </button>
        </div>
      )}

      {showFocusOptions && (
        <div className="focus-options">
          {showPicker ? (
            <FocusPicker
              storyId={storyId}
              projectId={projectId}
              seriesAltitude={seriesAltitude}
              selectedTemplate={template !== 'custom' ? template : null}
              selectedCustomFocus={customFocus}
              onSelectTemplate={handleTemplateSelect}
              onSelectCustomFocus={handleCustomFocusSelect}
            />
          ) : (
            <>
              <div className="focus-type-grid">
                {templates.map(type => (
                  <button
                    key={type.key}
                    className={`focus-type-btn ${template === type.key ? 'active' : ''}`}
                    onClick={() => handleTemplateSelect(type.key)}
                    disabled={type.key === 'logline' && existingLoglineSession}
                  >
                    <span className="type-label">{type.label}</span>
                    <span className="type-desc">{type.description}</span>
                    {type.key === 'logline' && existingLoglineSession && (
                      <span className="type-note">Already exists</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {template && template !== 'logline' && template !== 'custom' && (
            <div className="focus-target-input">
              <label>
                {template === 'character' && (seriesAltitude
                  ? "Who is this person? (name or role)"
                  : "Who is this character? (role or name)")}
                {template === 'world' && (seriesAltitude
                  ? "Which rules or setting?"
                  : "What element are you focusing on?")}
                {template === 'series_theme' && "What's the theme statement? (optional)"}
                {template === 'scene' && "Which scene?"}
                {template === 'plot' && "Which part of the argument?"}
                {template === 'dialogue' && "Whose voice?"}
              </label>
              <input
                type="text"
                value={focusTargetName}
                onChange={(e) => onFocusTargetNameChange(e.target.value)}
                placeholder={seriesAltitude && template === 'character'
                  ? "Optional — ending can stay blank"
                  : "Optional - you can discuss this in the session"}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Named export for backward compatibility
export { GuidanceModeSelector };
